<div
  class="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-card shadow-xl dark:bg-zinc-900"
  [overlayLoader]="loadingLookups() || saving() || uploading()">
  <div
    class="dialog-header sticky top-0 z-10 shrink-0 !m-0 !px-6 !py-4 bg-primary text-white shadow-sm flex items-start justify-between gap-3">
    <div class="flex items-center gap-3 min-w-0">
      <div class="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
        <mat-icon class="icon-size-6 !text-white" [svgIcon]="'heroicons_outline:banknotes'"></mat-icon>
      </div>
      <div class="dialog-title min-w-0">
        <h2 class="!text-white !m-0 text-lg font-bold tracking-tight">Pay invoice</h2>
        <p class="dialog-subtitle !text-white/80 !m-0 text-sm mt-0.5 truncate">
          {{ titleHint }} — posts expense and cash/bank
        </p>
      </div>
    </div>
    <button
      mat-icon-button
      type="button"
      class="!text-white hover:!bg-white/10 shrink-0"
      aria-label="Close"
      (click)="cancel()">
      <mat-icon class="!text-white" [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
    </button>
  </div>

  <mat-dialog-content class="!m-0 min-h-0 flex-1 overflow-y-auto !px-6 !py-5 min-w-0">
    <form [formGroup]="form" class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start min-w-0 [&>*]:min-w-0">
      <mat-form-field class="fuse-mat-dense w-full min-w-0 sm:col-span-2" subscriptSizing="dynamic">
        <mat-label>Expense account</mat-label>
        <app-custom-auto-complete
          formControlName="expenseAccountId"
          [options]="expenseAccounts"
          valueKey="id"
          [optionLabelWith]="accountOptionLabel"
          [displayWith]="displayExpenseFn"
          [compareWith]="idCompare"
          placeholder="Select expense account..." />
        @if (form.get('expenseAccountId')?.hasError('required') && form.get('expenseAccountId')?.touched) {
          <mat-error>Expense account is required</mat-error>
        }
      </mat-form-field>

      <mat-form-field class="fuse-mat-dense w-full min-w-0" subscriptSizing="dynamic">
        <mat-label>Payment method</mat-label>
        <app-custom-auto-complete
          formControlName="paymentMethod"
          [options]="paymentMethodOptions"
          valueKey="value"
          labelKey="label"
          [displayWith]="displayPaymentMethodFn"
          [compareWith]="paymentMethodCompare"
          placeholder="Bank" />
      </mat-form-field>

      <mat-form-field class="fuse-mat-dense w-full min-w-0" subscriptSizing="dynamic">
        <mat-label>{{
          form.get('paymentMethod')?.value === paymentMethodCash ? 'Cash account' : 'Bank account'
        }}</mat-label>
        <app-custom-auto-complete
          formControlName="paymentAccountId"
          [options]="paymentAccounts"
          valueKey="id"
          [optionLabelWith]="accountOptionLabel"
          [displayWith]="displayPaymentAccountFn"
          [compareWith]="idCompare"
          placeholder="Select account..." />
        @if (form.get('paymentAccountId')?.hasError('required') && form.get('paymentAccountId')?.touched) {
          <mat-error>Payment account is required</mat-error>
        }
      </mat-form-field>

      <mat-form-field class="fuse-mat-dense w-full min-w-0" subscriptSizing="dynamic">
        <mat-label>Amount</mat-label>
        <input matInput type="number" min="0.01" step="0.01" formControlName="amount" />
        @if (form.get('amount')?.hasError('required') && form.get('amount')?.touched) {
          <mat-error>Amount is required</mat-error>
        }
        @if (form.get('amount')?.hasError('min') && form.get('amount')?.touched) {
          <mat-error>Amount must be greater than zero</mat-error>
        }
      </mat-form-field>

      <mat-form-field class="fuse-mat-dense w-full min-w-0" subscriptSizing="dynamic">
        <mat-label>Payment date</mat-label>
        <input matInput [matDatepicker]="payPicker" formControlName="paymentDate" />
        <mat-datepicker-toggle matIconSuffix [for]="payPicker"></mat-datepicker-toggle>
        <mat-datepicker #payPicker></mat-datepicker>
        @if (form.get('paymentDate')?.hasError('required') && form.get('paymentDate')?.touched) {
          <mat-error>Payment date is required</mat-error>
        }
      </mat-form-field>

      <mat-form-field class="fuse-mat-dense w-full min-w-0 sm:col-span-2" subscriptSizing="dynamic">
        <mat-label>Remarks</mat-label>
        <textarea matInput rows="2" formControlName="remarks" placeholder="Optional"></textarea>
      </mat-form-field>

      <div
        class="sm:col-span-2 flex flex-col gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-2.5 min-w-0">
        <div class="flex items-center gap-3 min-w-0">
          <input #fileInput type="file" class="hidden" (change)="onFileChange($event)" />
          <button mat-stroked-button type="button" [disabled]="uploading()" (click)="fileInput.click()">
            <mat-icon class="icon-size-5" [svgIcon]="'heroicons_outline:paper-clip'"></mat-icon>
            <span class="ms-2">{{ fileName() ? 'Replace file' : 'Attach proof' }}</span>
          </button>
          @if (fileName(); as name) {
            <span class="min-w-0 flex-1 truncate text-sm text-secondary">{{ name }}</span>
            <button mat-icon-button type="button" aria-label="Remove file" (click)="removeFile()">
              <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
            </button>
          } @else {
            <span class="text-sm text-secondary">Optional</span>
          }
        </div>
        <p class="text-xs text-secondary leading-relaxed m-0">
          Optional — not required to pay. You can skip this and attach proof later.
        </p>
      </div>
    </form>
  </mat-dialog-content>

  <div class="sticky bottom-0 z-10 shrink-0 bg-card px-6 pb-5 pt-2 dark:bg-zinc-900">
    <mat-dialog-actions align="end" class="!m-0 !p-0 min-h-0 gap-2">
      <button mat-stroked-button type="button" [disabled]="saving()" (click)="cancel()">Cancel</button>
      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="saving() || uploading() || loadingLookups()"
        (click)="submit()">
        {{ saving() ? 'Paying…' : 'Pay invoice' }}
      </button>
    </mat-dialog-actions>
  </div>
</div>
